// A single Hidden Gem card: large preview image on top, title, one-line
// description, italic "why it's a gem" line, and a quiet footer row.
//
// Performance behaviour:
//  - off-screen cards are skipped by the renderer (content-visibility)
//  - previews two-to-three rows ahead of the viewport are fetched AND decoded
//    early, so they paint the instant they scroll in
//  - detail data is warmed on visibility (idle queue) and on pointer intent
//  - navigation starts on pointerdown for a free head start over click

import { memo, useEffect, useRef, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { getLibraryItemBySlug } from "@/lib/library.functions";
import { enqueuePrefetch, prefetchNow } from "@/lib/prefetch-queue";
import { formatCount, isLiked, toggleLike } from "@/lib/library-engagement";

type LibraryCardProps = {
  slug: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  tags: string[];
  author: string;
  license: string;
  ogImageUrl: string;
  whyGem?: string;
  /** UTC epoch day the gem was added — drives the freshness badge. */
  addedDay?: number;
  views?: number;
  likes?: number;
  /** First row of the grid loads eagerly; everything else lazily. */
  eager?: boolean;
  /** Current search term — the matching part of the title is emphasised. */
  query?: string;
};


/** Emphasise the matched substring so the ranking is visible in the title. */
function highlight(text: string, query?: string) {
  const q = query?.trim();
  if (!q || q.length < 2) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-foreground/10 text-foreground rounded-[3px] px-0.5">
        {text.slice(i, i + q.length)}
      </mark>
      {text.slice(i + q.length)}
    </>
  );
}


function hueFor(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

// Deep gradient — only for the no-image fallback tile, where white initials
// need contrast. It paints immediately, so it never reads as a flash.
function gradientFor(name: string): string {
  const hue = hueFor(name);
  return `linear-gradient(135deg, oklch(0.32 0.12 ${hue}), oklch(0.22 0.08 ${(hue + 40) % 360}))`;
}

// Light tint used while a real preview is still decoding: a shade of the page,
// never a hole in it.
function tintFor(name: string): string {
  const hue = hueFor(name);
  return `linear-gradient(135deg, oklch(0.965 0.012 ${hue}), oklch(0.93 0.018 ${(hue + 40) % 360}))`;
}

function initialsFor(name: string): string {
  const parts = name.replace(/[^a-zA-Z0-9 ]/g, "").trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** The pipeline stores a 480px card and a 1000px full render side by side. */
function srcSetFor(url: string): string | undefined {
  if (!url.endsWith("-480.webp")) return undefined;
  const full = url.replace(/-480\.webp$/, "-1000.webp");
  return `${url} 480w, ${full} 1000w`;
}

export const LibraryCard = memo(function LibraryCard({
  slug,
  name,
  description,
  category,
  subcategory,
  tags,
  author,
  license,
  ogImageUrl,
  whyGem,
  addedDay: _addedDay = 0,
  views = 0,
  likes = 0,
  eager = false,
  query,
}: LibraryCardProps) {
  const hasImg = ogImageUrl && ogImageUrl.length > 0;
  const queryClient = useQueryClient();
  const router = useRouter();
  const rootRef = useRef<HTMLAnchorElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(eager);
  // A preview already in the browser cache should appear at full opacity with
  // no transition at all — the common case on back-navigation.
  const [instant, setInstant] = useState(false);
  // Square previews render at their full 1:1 aspect in the masonry grid
  // (capped so the card never grows unbounded); landscape previews stay 2:1.
  const [square, setSquare] = useState(false);
  // Once the card is within the anticipation band we stop skipping its paint,
  // so the slot and its image are rendered before it becomes visible.
  const [near, setNear] = useState(eager);
  // Hearts come from localStorage, so they must not run during SSR.
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(likes);

  useEffect(() => {
    setLiked(isLiked(slug));
  }, [slug]);

  useEffect(() => {
    setLikeCount(likes);
  }, [likes]);

  const onLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => Math.max(0, c + (next ? 1 : -1)));
    void toggleLike(slug, next).then((total) => {
      if (typeof total === "number") setLikeCount(total);
    });
  };

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setInstant(true);
      setLoaded(true);
      const r = img.naturalWidth / img.naturalHeight;
      setSquare(r >= 0.9 && r <= 1.1);
    }
  }, [ogImageUrl]);

  const imgAspect = square ? "aspect-square max-h-[420px]" : "aspect-[2/1]";

  const warmData = () =>
    queryClient.prefetchQuery({
      queryKey: ["library-item", slug],
      queryFn: () => getLibraryItemBySlug({ data: { slug } }),
      staleTime: 5 * 60 * 1000,
    });

  // Warm the detail page's data on intent so the click feels instant.
  const prefetch = () => prefetchNow(`item:${slug}`, warmData);

  // Anticipation: as soon as the card comes within ~2 rows of the viewport,
  // force-load and decode its preview; once it's actually visible, queue the
  // detail data behind the idle prefetch queue.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const warmImage = () => {
      const img = imgRef.current;
      if (!img) return;
      if (img.loading === "lazy") img.loading = "eager";
      img.fetchPriority = "auto";
      if (img.complete) {
        setLoaded(true);
        return;
      }
      img.decode?.().then(
        () => setLoaded(true),
        () => undefined,
      );
    };

    const nearIO = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        setNear(true);
        warmImage();
        nearIO.disconnect();
      },
      { rootMargin: "1400px 0px" },
    );
    nearIO.observe(el);

    const visible = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        enqueuePrefetch(`item:${slug}`, warmData);
        visible.disconnect();
      },
      { threshold: 0.5 },
    );
    visible.observe(el);

    return () => {
      nearIO.disconnect();
      visible.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return (
    <Link
      ref={rootRef}
      to="/library/$slug"
      params={{ slug }}
      preload="intent"
      onMouseEnter={prefetch}
      onFocus={prefetch}
      onTouchStart={prefetch}
      onPointerDown={(e) => {
        prefetch();
        // Left click / primary touch: start routing ~80ms before click fires.
        if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
          void router.preloadRoute({ to: "/library/$slug", params: { slug } });
        }
      }}
      style={
        near
          ? { containIntrinsicSize: "auto 420px" }
          : { contentVisibility: "auto", containIntrinsicSize: "auto 420px" }
      }
      className="library-card-motion group relative isolate block rounded-3xl bg-card p-2 ring-1 ring-border/60 overflow-hidden hover:shadow-[0_1px_2px_rgba(20,20,40,0.04),0_10px_24px_-18px_rgba(20,20,40,0.22),0_22px_54px_-42px_rgba(20,20,40,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-gem
      aria-label={`${name} — ${category}`}
    >
      {/* Colour glow: a heavily blurred copy of the preview bleeding downwards
          behind the image panel. Purely decorative, clipped by the card. */}
      {hasImg ? (
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-x-2 top-2 -z-10 ${imgAspect}`}
        >
          <img
            src={ogImageUrl}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            className={`h-full w-full scale-[1.1] object-cover blur-[75px] saturate-150 transition-opacity duration-700 ${
              loaded ? "opacity-70" : "opacity-0"
            }`}
          />
        </div>
      ) : null}

      <div
        className={`relative ${imgAspect} overflow-hidden rounded-2xl bg-muted`}
        style={hasImg ? { background: tintFor(name) } : undefined}
      >
        {hasImg ? (
          <div className="absolute inset-0 transform-gpu transition-transform duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] [backface-visibility:hidden] [will-change:transform] group-hover:scale-[1.05]">
            <img
              ref={imgRef}
              src={ogImageUrl}
              srcSet={srcSetFor(ogImageUrl)}
              sizes="(min-width: 1280px) 320px, (min-width: 1024px) 30vw, (min-width: 640px) 45vw, 92vw"
              alt={`Preview of ${name}`}
              width={480}
              height={240}
              loading={eager ? "eager" : "lazy"}
              fetchPriority={eager ? "high" : "low"}
              decoding="async"
              onLoad={(e) => {
                const t = e.currentTarget;
                if (t.naturalWidth && t.naturalHeight) {
                  const r = t.naturalWidth / t.naturalHeight;
                  setSquare(r >= 0.9 && r <= 1.1);
                }
                setLoaded(true);
              }}
              className={`block h-full w-full object-cover ${
                instant ? "" : "transition-opacity duration-[450ms]"
              } ${loaded ? "opacity-100" : "opacity-0"}`}
            />
          </div>
        ) : (

          <div
            className="flex aspect-[2/1] w-full items-center justify-center"
            style={{ background: gradientFor(name) }}
          >
            <span className="font-display text-5xl tracking-tight text-white/90">
              {initialsFor(name)}
            </span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
      </div>

      <div className="flex flex-col gap-2 px-3 pb-3 pt-4">
        <div className="flex items-center justify-between gap-2">
          <h3
            className="min-w-0 truncate font-display text-xl leading-tight tracking-[-0.02em] text-foreground"
          >
            {highlight(name, query)}
          </h3>
          <button
            type="button"
            onClick={onLike}
            aria-pressed={liked}
            aria-label={liked ? `Unlike ${name}` : `Like ${name}`}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 tabular-nums transition-[color,background-color,transform] duration-200 hover:bg-muted active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              liked ? "text-rose-500" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <svg
              viewBox="0 0 20 20"
              aria-hidden
              className={`h-3.5 w-3.5 transition-transform duration-300 ${liked ? "scale-110" : ""}`}
              fill={liked ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M10 16.5S3.5 12.6 3.5 8.4A3.4 3.4 0 0 1 10 6.7a3.4 3.4 0 0 1 6.5 1.7c0 4.2-6.5 8.1-6.5 8.1Z" strokeLinejoin="round" />
            </svg>
            {likeCount > 0 ? formatCount(likeCount) : ""}
          </button>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground line-clamp-2 text-pretty">
          {description}
        </p>
        {whyGem ? (
          <p className="text-sm italic leading-relaxed text-foreground/70 line-clamp-1">
            {whyGem}
          </p>
        ) : null}
        <div className="mt-1 flex items-center justify-between gap-3 border-t border-border/50 pt-3 text-xs text-muted-foreground">
          <span className="truncate">{author || "Unknown"}</span>
          <span className="flex shrink-0 items-center gap-2">
            {views >= 25 ? (
              <span className="tabular-nums">{formatCount(views)} views</span>
            ) : null}
            {license ? (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide">
                {license}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </Link>
  );
});
